import type { Context } from '@fortune-sheet/core';
import { isEditingInContext, isFilterMenuVisible, type WorkbookInternals } from './workbookInternals';
import {
    CELL_INPUT_ID,
    COPY_MARQUEE_SELECTOR,
    CONTEXT_MENU_SELECTOR,
    TOOLBAR_POPUP_SELECTOR,
    LIBRARY_MODAL_SELECTOR,
    POPOVER_BACKDROP_SELECTOR,
    MODAL_CLOSE_ICON_SELECTOR,
    MODAL_CANCEL_BUTTON_SELECTOR,
} from './selectors';

/** Everything the Esc policy needs to decide an outcome. Built by the
 *  Lane B handler from `getContext()` when the patch is present, or from DOM
 *  probes when it is not (see `readEscStateFromDom`). */
export interface EscState {
    workbookMounted: boolean;
    nameBoxTarget: boolean;
    dialogOpen: boolean;
    editing: boolean;
    contextMenu: boolean;
    filterMenu: boolean;
    searchOpen: boolean;
    copyMarquee: boolean;
    /** True while the KeyTips overlay (Lane K) is armed/open. */
    keytipsOpen: boolean;
    /** A Fortune toolbar dropdown/combo popup or the "More" overflow
     *  panel is visible (`.fortune-toolbar-combo-popup`, `.fortune-toolbar-more-container`). */
    toolbarPopupOpen: boolean;
    /** One of Fortune's own `ModalProvider` dialogs is open (e.g. the
     *  format dropdown's "More formats…", `.fortune-modal-container`). */
    libraryModalOpen: boolean;
    /** Esc's target is the sheet-tab rename contentEditable span. */
    tabRenameTarget: boolean;
}

export type EscOutcome =
    | 'pass-through'
    | 'close-dialog'
    | 'close-menu'
    | 'close-drawer'
    | 'close-keytips'
    | 'cancel-rename'
    | 'close-library-dialog';

/**
 * Pure Esc policy. First match wins. Escape reaches this BEFORE the library's
 * own handlers (Lane B runs in capture), so it always reads state from before
 * the cancel.
 */
export const resolveEscape = (s: EscState): EscOutcome => {
    if (s.keytipsOpen) return 'close-keytips'; // rule 0: KeyTips owns Esc while open
    // A dialog (e.g. Rename) can be open while the grid is still loading or
    // errored — it must close on its own Esc rather than the not-mounted
    // check closing the whole drawer out from under it.
    if (s.dialogOpen) return 'close-dialog';
    if (!s.workbookMounted) return 'close-drawer';
    if (s.nameBoxTarget) return 'pass-through';
    if (s.tabRenameTarget) return 'cancel-rename';
    if (s.editing) return 'pass-through'; // library InputBox cancels
    if (s.libraryModalOpen) return 'close-library-dialog';
    if (s.contextMenu || s.filterMenu || s.searchOpen || s.toolbarPopupOpen) return 'close-menu'; // library Esc is dead here (core:77151-77153)
    if (s.copyMarquee) return 'pass-through'; // library clears (core:77154-77156)
    return 'close-drawer';
};

/** `close-menu` recipe (mirrors the backdrop handler, react:11299-11303). */
export const closeMenusRecipe = (d: Context): void => {
    d.contextMenu = {};
    d.filterContextMenu = undefined;
    d.showSearch = false;
    d.showReplace = false;
    d.showSheetList = undefined; // mirrors the backdrop's own mousedown recipe (react:11310-11316)
};

/** Degraded-mode DOM probe for the copy marquee:
 *  `.fortune-selection-copy` is the library's own marquee overlay class. */
const isCopyMarqueeVisibleInDom = (): boolean => Boolean(document.querySelector(COPY_MARQUEE_SELECTOR));

/** Degraded-mode DOM probe for "is a cell being edited": the InputBox sits at
 *  inline `z-index: 19` while editing (there is no other reliable class). */
const isEditingInDom = (): boolean => {
    const el = document.getElementById(CELL_INPUT_ID);
    return el?.style.zIndex === '19';
};

/** Degraded-mode DOM probe for an open right-click context menu. There is no
 *  search/replace probe — without the patch, Esc simply falls through to
 *  the library's own Find/Replace handler for that one case. */
const isContextMenuVisibleInDom = (): boolean => Boolean(document.querySelector(CONTEXT_MENU_SELECTOR));

/** Neither a Fortune toolbar popup nor a `ModalProvider` dialog has
 *  any context/API equivalent — always a DOM probe, in both modes. Scoped to
 *  the drawer's own `wrapperRef` (the same element `useKeyTips` already
 *  queries) rather than `document` — Fortune's toolbar/modals always render
 *  inside it, never via a portal elsewhere — so a decoy element belonging to
 *  a different sheet editor instance is never matched. A null root (not yet
 *  mounted) means neither probe can ever be true; there is no document
 *  fallback. */
const isToolbarPopupVisibleInDom = (root: HTMLElement | null): boolean => {
    const el = root?.querySelector<HTMLElement>(TOOLBAR_POPUP_SELECTOR);
    return Boolean(el && el.offsetParent !== null);
};
const isLibraryModalOpenInDom = (root: HTMLElement | null): boolean => Boolean(root?.querySelector(LIBRARY_MODAL_SELECTOR));

/**
 * Builds `EscState` for the Lane B handler. Reads `getContext()` when the
 * patch is present; otherwise falls back to DOM probes for the 3 fields that
 * have no public API equivalent (editing / menus / marquee), plus the 2
 * toolbar/modal DOM-probe fields (neither has a context field). `nameBoxTarget`,
 * `dialogOpen` and `tabRenameTarget` are always read from the app's own
 * state (never the library), and `workbookMounted` is always passed in
 * directly. `root` is the drawer's own wrapper element (`null` before mount).
 */
export const buildEscState = (
    internals: WorkbookInternals | null,
    args: {
        workbookMounted: boolean;
        nameBoxTarget: boolean;
        dialogOpen: boolean;
        keytipsOpen?: boolean;
        tabRenameTarget?: boolean;
        root: HTMLElement | null;
    },
): EscState => {
    const shared = {
        workbookMounted: args.workbookMounted,
        nameBoxTarget: args.nameBoxTarget,
        dialogOpen: args.dialogOpen,
        keytipsOpen: Boolean(args.keytipsOpen),
        tabRenameTarget: Boolean(args.tabRenameTarget),
        toolbarPopupOpen: isToolbarPopupVisibleInDom(args.root),
        libraryModalOpen: isLibraryModalOpenInDom(args.root),
    };
    const ctx = internals?.getContext();
    if (ctx) {
        return {
            ...shared,
            editing: isEditingInContext(ctx),
            contextMenu: Boolean(ctx.contextMenu && Object.keys(ctx.contextMenu).length > 0),
            filterMenu: ctx.filterContextMenu != null && isFilterMenuVisible(document),
            searchOpen: Boolean(ctx.showSearch || ctx.showReplace),
            copyMarquee: (ctx.luckysheet_selection_range?.length ?? 0) > 0,
        };
    }
    return {
        ...shared,
        editing: isEditingInDom(),
        contextMenu: isContextMenuVisibleInDom(),
        filterMenu: isFilterMenuVisible(document),
        searchOpen: false,
        copyMarquee: isCopyMarqueeVisibleInDom(),
    };
};

/**
 * Degraded-mode `close-menu`: without the patch there is no
 * `setContextWithProduce` to run `closeMenusRecipe` through, so the shim
 * mirrors the library's own backdrop handler (`react:11310-11316`) by
 * dispatching the SAME event type it listens for — `mousedown`, not `click`.
 * Returns false (a no-op) when no backdrop is present.
 */
export const dispatchDegradedCloseMenu = (): boolean => {
    const backdrop = document.querySelector(POPOVER_BACKDROP_SELECTOR);
    if (!backdrop) return false;
    backdrop.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
    return true;
};

/** `close-menu` addition: every Fortune toolbar popup and the "More"
 *  overflow panel close themselves via `useOutsideClick` (react:3239-3251),
 *  which is a plain `document` `mousedown` listener checking `!container.contains(e.target)` —
 *  dispatched on `document.body` unconditionally alongside the existing
 *  context/filter-menu recipe, since neither handles toolbar popups. */
export const dispatchOutsideClickClose = (): void => {
    document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
};

/** `cancel-rename`: restores Fortune's own pre-edit snapshot
 *  (`dataset.oldText`, set when editing starts, react:6965) then blurs —
 *  the library's own `onBlur` (`core.editSheetName`, react:6968) then commits
 *  the now-restored, unchanged name. */
export const cancelTabRename = (target: HTMLElement): void => {
    if (target.dataset.oldText != null) target.innerText = target.dataset.oldText;
    target.blur();
};

/** `close-library-dialog`: Fortune's `ModalProvider` dialogs
 *  (e.g. the format dropdown's "More formats…") close via their header close
 *  icon; a dialog with none falls back to its own Cancel button. Scoped to
 *  `root` — same rationale as `isLibraryModalOpenInDom` above (Fortune's
 *  modals always render inside the drawer's own wrapper, never via a
 *  portal), so this never clicks a modal belonging to a different sheet
 *  editor instance. */
export const closeLibraryModal = (root: HTMLElement | null): boolean => {
    const closeIcon = root?.querySelector<HTMLElement>(MODAL_CLOSE_ICON_SELECTOR);
    if (closeIcon) {
        closeIcon.click();
        return true;
    }
    const cancelBtn = root?.querySelector<HTMLElement>(MODAL_CANCEL_BUTTON_SELECTOR);
    if (cancelBtn) {
        cancelBtn.click();
        return true;
    }
    return false;
};
